import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.jsx";
import {
  ChakraProvider,
  extendTheme,
  defineStyleConfig,
} from "@chakra-ui/react";
import { BrowserRouter } from "react-router";
import FirebaseWrapper from "./wrappers/firebase";
import { createMultiStyleConfigHelpers } from "@chakra-ui/styled-system";

const linkTheme = defineStyleConfig({
  baseStyle: {
    fontWeight: "semibold",
    color: "green.400",
    _hover: {
      textDecor: "none",
      color: "white",
    },
  },
});

const inputTheme = defineStyleConfig({
  variants: {
    outline: {
      field: {
        bg: "#1a1a1d",
        borderRadius: "md",
        borderColor: "#303036",
        _hover: {
          borderColor: "#3e3e46",
        },
        fontSize: ".875rem",
        fontWeight: "normal",
        color: "white",
      },
    },
    "no-outline": {
      field: {
        bg: "inherit",
        borderRadius: "md",
        border: "none",
        _focus: {
          boxShadow: "none",
        },
      },
    },
  },
  defaultProps: {
    variant: "outline",
  },
});
const textAreaTheme = defineStyleConfig({
  variants: {
    outline: { ...inputTheme.variants.outline.field },
  },
  defaultProps: {
    variant: "outline",
  },
});

const buttonTheme = defineStyleConfig({
  variants: {
    secondary: {
      transition: "none",
      bg: "#313134",
      fontSize: "13px",
      border: "1px dashed",
      borderColor: "gray.600",
      color: "white",
      _hover: {
        bg: "white",
        color: "black",
        borderColor: "white",
      },
    },
    nav: {
      transition: "none",
      w: "auto",
      // p: "10px 12px",
      borderRadius: "md",
      color: "white",
      fontWeight: "medium",
      fontSize: "13px",
      bg: "#2a2a2c",
      // h: "30px",
      minH: "0",
      _hover: {
        bg: "#313134",
        color: "white",
      },
      _active: {
        bg: "#313134",
        color: "white",
      },
      cursor: "default",
    },
    nav_collapsed: {
      transition: "background none",
      w: "auto",
      px: "2",
      pr: "0",
      borderRadius: "md",
      color: "white",
      fontWeight: "medium",
      fontSize: "15px",
      bg: "#2e2e30",
      h: "35px",
      _hover: {
        bg: "white",
        color: "black",
      },
      cursor: "default",
      _active: {
        bg: "white",
      },
    },
    solid: (props) => ({
      color: "black",
      bg: "white",
      gap: "0.5",
      transition: "gap 300ms",
      cursor: "pointer",
      _hover: {
        bg: "black",
        color: "white",
        // transform: "scale(0.95)",
      },
      _active: {
        bg: "black",
        color: "white",
      },
      _disabled: {
        _hover: {
          color: "black",
        },
      },
      _loading: {
        color: "#006139",
      },
    }),
    glow: {
      color: "gray.200",
      bg: "transparent",
      // gap: "0",
      transition: "all 150ms",
      textShadow: "0",
      filter: "drpo-shadow(0)",
      _hover: {
        // gap: "0.5",
        color: "white",
        bg: "transparent",
        filter:
          "drop-shadow(0 0 2px rgba(255,255,255,0.4)) drop-shadow(0 0 4px rgba(255,255,255,0.2)) drop-shadow(0 0 8px rgba(255,255,255,0.1))",
      },
      _active: {
        bg: "transparent",
      },
    },
    link: {
      borderBottom: "1px solid",
      borderBottomColor: "white",
      bg: "transparent",
      _hover: {
        bg: "transparent",
      },
    },
  },
  defaultProps: {
    variant: "solid",
  },
});

const menuTheme = defineStyleConfig({
  baseStyle: {
    transitionDuration: "fast",
    list: {
      background: "#212123",
      border: "1px solid #3e3e46",
      w: "150px",
      p: "1",
    },
    item: {
      minH: "32px",
      h: "max-content",
      columnGap: 2,
      background: "#212123",
      fontSize: "12px",
      fontWeight: "medium",
      transition: "none",
      _hover: {
        background: "#313134",
      },
      fontFamily: `'Inter Variable', sans-serif`,
    },
  },
});

const drawerTheme = defineStyleConfig({
  baseStyle: {
    dialog: {
      bg: "#212123",
      borderRadius: "xl",
      m: "2",
      px: 3,
      pl: 4,
    },
    header: {
      color: "white",
      display: "flex",
      justifyContent: "space-between",
      px: 0,
      pl: 1,
    },
    footer: {
      px: 0,
      columnGap: 2,
    },
    body: {
      px: 0,
    },
    closeButton: {
      color: "white",
      _hover: {
        bg: "#313134",
      },
      pos: "relative",
      top: 0,
      right: 0,
    },
  },
});

const modalTheme = defineStyleConfig({
  baseStyle: {
    dialog: {
      bg: "#212123",
      // backdropFilter: "blur(18px)",
      borderRadius: "xl",
      px: 3,
    },
    header: {
      color: "white",
      display: "flex",
      justifyContent: "space-between",
      px: 0,
      pl: 2,
    },
    footer: {
      px: 0,
      columnGap: 2,
    },
    body: {
      p: 0,
      color: "white",
    },
    closeButton: {
      color: "white",
      _hover: {
        bg: "#313134",
      },
      pos: "relative",
      top: 0,
      right: 0,
    },
  },
});

const tabStyle = defineStyleConfig({
  baseStyle: {
    tabpanel: {
      px: 0,
    },
    tablist: {
      bg: "#212123",
      borderRadius: "md",
      border: "1px solid #2a2a2c",
      p: "0.5",
      h: "32px",
    },
  },
  variants: {
    custom: {
      tab: {
        transition: "all 200ms",
        fontSize: "14px",
        color: "gray",
        _selected: {
          color: "white",
          bg: "#2a2a2c",
          fontWeight: "semibold",
        },
      },
    },
  },
  defaultProps: {
    variant: "custom",
  },
});

const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers(["content", "arrow"]);

const tooltipBaseStyle = definePartsStyle({
  content: {
    bg: "white",
    color: "black",
    borderRadius: "md",
    border: "1px solid #3e3e46",
    boxShadow: "none",
    px: 2,
    py: 1,
    fontSize: "12px",
    fontWeight: "medium",
  },
  arrow: {
    bg: "white",
  },
});

const tooltipTheme = defineMultiStyleConfig({
  baseStyle: tooltipBaseStyle,
  defaultProps: {
    hasArrow: true,
    placement: "top",
    openDelay: 300,
  },
});

const tableTheme = defineStyleConfig({
  baseStyle: {
    table: {
      bg: "#212123",
      borderRadius: "md",
      border: "1px solid #2a2a2c",
      overflow: "hidden",
    },
    thead: {
      bg: "#2a2a2c",
    },
    tbody: {
      bg: "#212123",
    },
    tr: {
      borderBottom: "1px solid #2a2a2c",
      _last: {
        borderBottom: "none",
      },
      _hover: {
        bg: "#313134",
      },
    },
    th: {
      color: "white",
      fontWeight: "semibold",
      fontSize: "13px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
      py: 3,
      px: 4,
      borderBottom: "1px solid #3e3e46",
      _first: {
        pl: 6,
      },
      _last: {
        pr: 6,
      },
    },
    td: {
      color: "gray.200",
      fontSize: "14px",
      fontWeight: "normal",
      py: 3,
      px: 4,
      _first: {
        pl: 6,
      },
      _last: {
        pr: 6,
      },
    },
    tfoot: {
      bg: "#2a2a2c",
      borderTop: "1px solid #3e3e46",
    },
  },
  variants: {
    striped: {
      tbody: {
        tr: {
          _odd: {
            bg: "#1a1a1d",
          },
          _even: {
            bg: "#212123",
          },
          _hover: {
            bg: "#313134",
          },
        },
      },
    },
    compact: {
      th: {
        py: 2,
        px: 3,
        fontSize: "12px",
        _first: {
          pl: 4,
        },
        _last: {
          pr: 4,
        },
        textTransform: "none",
      },
      td: {
        py: 2,
        px: 3,
        fontSize: "13px",
        _first: {
          pl: 4,
        },
        _last: {
          pr: 4,
        },
      },
    },
    simple: {
      table: {
        border: "none",
        bg: "transparent",
      },
      thead: {
        bg: "#2a2a2c",
      },
      tbody: {
        bg: "transparent",
      },
      tr: {
        borderBottom: "1px solid #2a2a2c",
        _hover: {
          bg: "#313134",
        },
      },
      th: {
        borderBottom: "none",
        color: "gray.400",
        fontWeight: "medium",
        textTransform: "none",
        letterSpacing: "normal",
      },
    },
  },
  defaultProps: {
    variant: "striped",
  },
});

const theme = extendTheme({
  fonts: {
    heading: `'Inter Variable', sans-serif`,
    body: `'Inter Variable', sans-serif`,
  },
  transition: {
    duration: {
      fast: "100ms",
      normal: "100ms",
    },
  },
  components: {
    Menu: menuTheme,
    Tabs: tabStyle,
    Input: inputTheme,
    Textarea: textAreaTheme,
    Button: buttonTheme,
    Link: linkTheme,
    Drawer: drawerTheme,
    Modal: modalTheme,
    Tooltip: tooltipTheme,
    Table: tableTheme,
  },
});

createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ChakraProvider theme={theme}>
      <BrowserRouter>
        <FirebaseWrapper
          hideEmulatorWarning={true}
          enableEmulator={import.meta.env.PROD ? false : true}
        >
          <App />
        </FirebaseWrapper>
      </BrowserRouter>
    </ChakraProvider>
  </React.StrictMode>
);
